/**
 * Role Permission Matrix Component
 * Tree-based permission editor using MODULE_TREE hierarchy.
 * Supports permissions at every tree level (module, category, feature).
 * Filtered by tenant module config so admins only see enabled features.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Alert,
  IconButton,
  Collapse,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { apiGet } from '../../utils/apiClient';
import {
  ACTIONS,
  ACTION_LABELS,
  MODULE_TREE,
  MODULE_CATEGORIES,
} from '../../contexts/PermissionConstants';
import { useTenant } from '../../contexts/TenantContext';

// Action keys as array for iteration
const ACTION_KEYS = Object.values(ACTIONS);

/**
 * Collect all dotted paths (including intermediate nodes) under a tree node.
 */
export const collectAllPaths = (node, prefix) => {
  if (!node.children) return [prefix];
  const paths = [prefix];
  for (const [key, child] of Object.entries(node.children)) {
    paths.push(...collectAllPaths(child, `${prefix}.${key}`));
  }
  return paths;
};

/**
 * Collect only leaf paths under a tree node.
 */
export const collectLeafPaths = (node, prefix) => {
  if (!node.children) return [prefix];
  const paths = [];
  for (const [key, child] of Object.entries(node.children)) {
    paths.push(...collectLeafPaths(child, `${prefix}.${key}`));
  }
  return paths;
};

/**
 * Get the check state for a specific action on a node.
 * Returns 'checked', 'unchecked', or 'indeterminate' based on children.
 */
export const getActionState = (node, path, action, permissions) => {
  const nodePerms = permissions[path];
  if (!node.children) {
    return nodePerms?.[action] ? 'checked' : 'unchecked';
  }
  const childKeys = Object.keys(node.children);
  let checkedCount = 0;
  let indeterminateCount = 0;
  for (const key of childKeys) {
    const childState = getActionState(node.children[key], `${path}.${key}`, action, permissions);
    if (childState === 'checked') checkedCount++;
    else if (childState === 'indeterminate') indeterminateCount++;
  }
  if (checkedCount === childKeys.length) return 'checked';
  if (checkedCount > 0 || indeterminateCount > 0) return 'indeterminate';
  return 'unchecked';
};

/**
 * Filter MODULE_TREE by tenant configuration.
 * Only shows modules and sub-features the tenant has enabled.
 */
export const filterTreeByTenant = (tree, isModuleEnabled, isFeatureEnabled) => {
  const filtered = {};
  for (const [code, node] of Object.entries(tree)) {
    if (!isModuleEnabled(code)) continue;
    if (node.children) {
      const filteredChildren = {};
      for (const [childKey, childNode] of Object.entries(node.children)) {
        if (!isFeatureEnabled(code, childKey)) continue;
        // Keep deeper children as-is (tenant config only goes 1 level deep currently)
        filteredChildren[childKey] = childNode;
      }
      if (Object.keys(filteredChildren).length > 0) {
        filtered[code] = { ...node, children: filteredChildren };
      } else {
        // Module enabled but all children disabled — show module without children
        filtered[code] = { label: node.label };
      }
    } else {
      filtered[code] = node;
    }
  }
  return filtered;
};

/**
 * Recursive tree row for a permission node
 */
export const PermTreeRow = ({
  node,
  path,
  depth,
  permissions,
  onToggleAction,
  readOnly,
  expanded,
  onToggleExpand,
}) => {
  const hasChildren = !!node.children;
  const isExpanded = expanded[path];

  // Compute action states
  const actionStates = useMemo(() => {
    const states = {};
    for (const action of ACTION_KEYS) {
      states[action] = getActionState(node, path, action, permissions);
    }
    return states;
  }, [node, path, permissions]);

  // Check if all actions are checked (for "All" chip)
  const allChecked = ACTION_KEYS.every((a) => actionStates[a] === 'checked');

  const handleExpandClick = () => {
    if (hasChildren) onToggleExpand(path);
  };

  return (
    <>
      <TableRow hover sx={{ '& td': { py: 0.25 } }}>
        <TableCell sx={{ pl: depth * 2.5 + 1, minWidth: 250, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {hasChildren ? (
              <IconButton size="small" onClick={handleExpandClick} sx={{ p: 0 }}>
                {isExpanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </IconButton>
            ) : (
              <Box sx={{ width: 24 }} />
            )}
            <Typography
              variant="body2"
              sx={{
                fontWeight: depth === 0 ? 600 : hasChildren ? 500 : 400,
                fontSize: depth === 0 ? '0.875rem' : '0.8125rem',
              }}
            >
              {node.label}
            </Typography>
            {allChecked && depth === 0 && (
              <Chip label="All" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
            )}
          </Box>
        </TableCell>
        {ACTION_KEYS.map((action) => (
          <TableCell key={action} align="center" padding="checkbox" sx={{ width: 75, minWidth: 75 }}>
            {readOnly ? (
              actionStates[action] === 'checked' ? (
                <CheckIcon fontSize="small" color="success" />
              ) : actionStates[action] === 'indeterminate' ? (
                <CheckIcon fontSize="small" color="warning" />
              ) : (
                <CloseIcon fontSize="small" color="disabled" />
              )
            ) : (
              <Checkbox
                checked={actionStates[action] === 'checked'}
                indeterminate={actionStates[action] === 'indeterminate'}
                onChange={() => onToggleAction(node, path, action)}
                size="small"
                sx={{ p: 0.25 }}
              />
            )}
          </TableCell>
        ))}
      </TableRow>
      {hasChildren && (
        <TableRow>
          <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Table size="small">
                <TableBody>
                  {Object.entries(node.children).map(([childKey, childNode]) => (
                    <PermTreeRow
                      key={childKey}
                      node={childNode}
                      path={`${path}.${childKey}`}
                      depth={depth + 1}
                      permissions={permissions}
                      onToggleAction={onToggleAction}
                      readOnly={readOnly}
                      expanded={expanded}
                      onToggleExpand={onToggleExpand}
                    />
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const RolePermissionMatrix = ({
  roleId,
  readOnly = false,
  onSave,
  onCancel,
  loading = false,
}) => {
  const [permissions, setPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({});
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});

  const { isModuleEnabled, isFeatureEnabled } = useTenant();

  // Filter tree by tenant config
  const visibleTree = useMemo(
    () => filterTreeByTenant(MODULE_TREE, isModuleEnabled, isFeatureEnabled),
    [isModuleEnabled, isFeatureEnabled]
  );

  // Build all visible paths for initialization
  const allVisiblePaths = useMemo(() => {
    const paths = [];
    for (const [code, node] of Object.entries(visibleTree)) {
      paths.push(...collectAllPaths(node, code));
    }
    return paths;
  }, [visibleTree]);

  // Group visible modules by category
  const categories = useMemo(() => {
    return Object.entries(MODULE_CATEGORIES).map(([catKey, moduleCodes]) => ({
      key: catKey,
      label: catKey === 'business' ? 'Business Functions' : 'Administration',
      modules: moduleCodes.filter((code) => visibleTree[code]),
    }));
  }, [visibleTree]);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        setLoadingData(true);
        setError(null);
        const response = await apiGet(`/admin/roles/${roleId}/permissions`);
        const perms = response.data || {};

        // Initialize all visible paths.
        // For each path, check exact match first, then fall back to nearest ancestor.
        // This handles existing data that only has top-level keys (e.g., 'contract_management')
        // by inheriting those permissions down to sub-feature paths.
        const fullPerms = {};
        for (const path of allVisiblePaths) {
          // Try exact match first
          let source = perms[path];
          // If no exact match, walk up to find nearest ancestor with permissions
          if (!source) {
            const parts = path.split('.');
            for (let i = parts.length - 1; i > 0; i--) {
              const ancestor = parts.slice(0, i).join('.');
              if (perms[ancestor]) {
                source = perms[ancestor];
                break;
              }
            }
          }
          fullPerms[path] = {
            read: source?.read || false,
            create: source?.create || false,
            update: source?.update || false,
            delete: source?.delete || false,
            approve: source?.approve || false,
          };
        }

        setPermissions(fullPerms);
        setOriginalPermissions(JSON.parse(JSON.stringify(fullPerms)));

        // Auto-expand top-level modules that have any permissions set
        const initialExpanded = {};
        for (const code of Object.keys(visibleTree)) {
          // Check if this module has any permissions (at any level)
          const hasAnyPerm = ACTION_KEYS.some((a) => fullPerms[code]?.[a]);
          if (hasAnyPerm) {
            initialExpanded[code] = true;
          }
        }
        setExpanded(initialExpanded);
      } catch (err) {
        setError('Failed to load permissions: ' + err.message);
      } finally {
        setLoadingData(false);
      }
    };

    if (roleId) {
      loadPermissions();
    }
  }, [roleId, allVisiblePaths, visibleTree]);

  const handleToggleExpand = useCallback((path) => {
    setExpanded((prev) => ({ ...prev, [path]: !prev[path] }));
  }, []);

  /**
   * Toggle an action on a node. Cascades to all descendants.
   */
  const handleToggleAction = useCallback(
    (node, path, action) => {
      if (readOnly) return;

      setPermissions((prev) => {
        const updated = { ...prev };
        const currentState = getActionState(node, path, action, prev);
        const newValue = currentState !== 'checked';

        // Set this node and all descendants
        const allPaths = collectAllPaths(node, path);
        for (const p of allPaths) {
          if (!updated[p]) {
            updated[p] = { read: false, create: false, update: false, delete: false, approve: false };
          }
          updated[p] = { ...updated[p], [action]: newValue };
        }

        return updated;
      });
    },
    [readOnly]
  );

  /**
   * Select all for a specific action column (all leaf nodes)
   */
  const handleSelectAllAction = useCallback(
    (action) => {
      if (readOnly) return;

      setPermissions((prev) => {
        // Check if all leaves have this action enabled
        const allLeaves = [];
        for (const [code, node] of Object.entries(visibleTree)) {
          allLeaves.push(...collectLeafPaths(node, code));
        }
        const allEnabled = allLeaves.every((p) => prev[p]?.[action]);
        const newValue = !allEnabled;

        const updated = { ...prev };
        // Set all paths (not just leaves, for consistent display)
        for (const path of allVisiblePaths) {
          if (!updated[path]) {
            updated[path] = { read: false, create: false, update: false, delete: false, approve: false };
          }
          updated[path] = { ...updated[path], [action]: newValue };
        }
        return updated;
      });
    },
    [readOnly, visibleTree, allVisiblePaths]
  );

  const hasChanges = () => {
    return JSON.stringify(permissions) !== JSON.stringify(originalPermissions);
  };

  const handleSave = () => {
    if (onSave) {
      // Only emit paths where at least one action is true
      const filtered = {};
      for (const [path, actions] of Object.entries(permissions)) {
        if (ACTION_KEYS.some((a) => actions[a])) {
          filtered[path] = actions;
        }
      }
      onSave(filtered);
    }
  };

  const handleReset = () => {
    setPermissions(JSON.parse(JSON.stringify(originalPermissions)));
  };

  const handleExpandAll = useCallback(() => {
    const allExpanded = {};
    for (const path of allVisiblePaths) {
      allExpanded[path] = true;
    }
    setExpanded(allExpanded);
  }, [allVisiblePaths]);

  const handleCollapseAll = useCallback(() => {
    setExpanded({});
  }, []);

  if (loadingData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          System roles cannot be modified. Clone this role to create a customizable copy.
        </Alert>
      )}

      {/* Expand/Collapse toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 0.5 }}>
        <Button size="small" variant="text" onClick={handleExpandAll}>
          Expand All
        </Button>
        <Button size="small" variant="text" onClick={handleCollapseAll}>
          Collapse All
        </Button>
      </Box>

      <TableContainer sx={{ maxHeight: 500 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 250 }}>
                Module / Feature
              </TableCell>
              {ACTION_KEYS.map((action) => (
                <TableCell
                  key={action}
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    cursor: readOnly ? 'default' : 'pointer',
                    width: 75,
                    '&:hover': !readOnly ? { backgroundColor: 'action.hover' } : {},
                  }}
                  onClick={() => !readOnly && handleSelectAllAction(action)}
                >
                  {ACTION_LABELS[action] || action}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((cat) => {
              if (cat.modules.length === 0) return null;
              return (
                <React.Fragment key={cat.key}>
                  {/* Category header */}
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      sx={{
                        backgroundColor: 'action.hover',
                        py: 0.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {cat.label}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {/* Module tree rows */}
                  {cat.modules.map((moduleCode) => (
                    <PermTreeRow
                      key={moduleCode}
                      node={visibleTree[moduleCode]}
                      path={moduleCode}
                      depth={0}
                      permissions={permissions}
                      onToggleAction={handleToggleAction}
                      readOnly={readOnly}
                      expanded={expanded}
                      onToggleExpand={handleToggleExpand}
                    />
                  ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {!readOnly && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              {hasChanges() && (
                <Chip
                  label="Unsaved changes"
                  color="warning"
                  size="small"
                  sx={{ mr: 1 }}
                />
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={handleReset}
                disabled={!hasChanges()}
              >
                Reset
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={onCancel}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading || !hasChanges()}
              >
                {loading ? <CircularProgress size={24} /> : 'Save Permissions'}
              </Button>
            </Box>
          </Box>
        </>
      )}

      {readOnly && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>Close</Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default RolePermissionMatrix;
